import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Pagination, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { Ellipsis, NoWrap } from 'pages/star-shared/components/status-tags';
import type { LogPerson, StudioFlagRow } from '../contract';
import { STUDIO_PAGES, StudioPaging } from '../api';
import {
  EMPTY_TEXT,
  FLAG_SEVERITY_COLOR,
  FLAG_SEVERITY_LABEL,
  FLAG_STATUS_LABEL,
  TIER_COLOR,
  TIER_LABEL,
  flagLabel,
} from '../labels';
import { formatDateTime } from '../format';
import CloseFlagModal, { CloseFlagTarget } from './close-flag-modal';
import styles from '../styles.module.scss';

export interface FlagTableProps {
  rows: StudioFlagRow[];
  loading?: boolean;
  /** Cột Studio; mặc định có (trang Giám sát). */
  showStudio?: boolean;
  /** ADMIN: nút "Khép cờ" trên cờ đang mở. */
  canClose?: boolean;
  onClosed?: () => void;
  pagination?: StudioPaging;
  emptyText?: string;
}

const PersonCell = ({ person }: { person: LogPerson | null }) =>
  person ? (
    <div>
      <div className={styles.cellTitle}>{person.name}</div>
      {person.tier && (
        <Tag color={TIER_COLOR[person.tier]} style={{ marginTop: 2 }}>
          {TIER_LABEL[person.tier]}
        </Tag>
      )}
    </div>
  ) : (
    <span>—</span>
  );

/** Bảng StudioFlagRow (mục 8.7.10: khối "Cờ cần xem" của Giám sát Studio). */
const FlagTable = ({
  rows,
  loading,
  showStudio = true,
  canClose,
  onClosed,
  pagination,
  emptyText = EMPTY_TEXT.monitorFlags,
}: FlagTableProps) => {
  const [closing, setClosing] = useState<CloseFlagTarget | null>(null);

  const columns: ColumnsType<StudioFlagRow> = [
    ...(showStudio
      ? ([
          {
            title: 'Studio',
            key: 'studio',
            width: 160,
            render: (_, r) =>
              r.studio ? (
                <Link to={STUDIO_PAGES.detail(r.studio.id)}>
                  {r.studio.title || `Studio #${r.studio.id}`}
                </Link>
              ) : (
                '—'
              ),
          },
        ] as ColumnsType<StudioFlagRow>)
      : []),
    {
      title: 'Mức',
      key: 'severity',
      width: 150,
      render: (_, r) => (
        <Tag color={FLAG_SEVERITY_COLOR[r.severity]}>{FLAG_SEVERITY_LABEL[r.severity]}</Tag>
      ),
    },
    {
      title: 'Cờ',
      key: 'code',
      width: 180,
      render: (_, r) => <span className={styles.cellTitle}>{flagLabel(r.code, r.label)}</span>,
    },
    {
      title: 'Người thao tác',
      key: 'actor',
      width: 170,
      render: (_, r) => <PersonCell person={r.actor} />,
    },
    {
      title: 'Người liên quan',
      key: 'subject',
      width: 170,
      render: (_, r) => <PersonCell person={r.subject} />,
    },
    {
      title: 'Chi tiết',
      key: 'detail',
      width: 240,
      render: (_, r) => <Ellipsis value={r.detail} width={240} />,
    },
    {
      title: 'Lúc',
      key: 'createdAt',
      width: 150,
      render: (_, r) => <NoWrap>{formatDateTime(r.createdAt)}</NoWrap>,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 130,
      render: (_, r) => {
        const tip =
          r.status !== 'OPEN' && (r.closedBy || r.closeNote)
            ? [
                r.closedBy ? `${r.closedBy.name} · ${formatDateTime(r.closedAt)}` : '',
                r.closeNote || '',
              ]
                .filter(Boolean)
                .join(' — ')
            : undefined;
        return (
          <Tooltip title={tip}>
            <Tag color={r.status === 'OPEN' ? 'red' : r.status === 'OK' ? 'green' : 'gold'}>
              {FLAG_STATUS_LABEL[r.status]}
            </Tag>
          </Tooltip>
        );
      },
    },
    ...(canClose
      ? ([
          {
            title: 'Thao tác',
            key: 'actions',
            fixed: 'right',
            width: 110,
            render: (_, r) =>
              r.status === 'OPEN' ? (
                <Button
                  size="small"
                  onClick={() =>
                    setClosing({
                      id: r.id,
                      code: r.code,
                      label: r.label,
                      severity: r.severity,
                      detail: r.detail,
                    })
                  }
                >
                  Khép cờ
                </Button>
              ) : null,
          },
        ] as ColumnsType<StudioFlagRow>)
      : []),
  ];

  return (
    <div className="custom-table">
      <Table<StudioFlagRow>
        columns={columns}
        rowKey="id"
        dataSource={rows}
        loading={loading}
        pagination={false}
        locale={{ emptyText }}
        scroll={{ x: 1400 }}
      />
      {pagination && pagination.total > pagination.pageSize && (
        <Pagination
          showSizeChanger={false}
          showLessItems
          current={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={pagination.onChange}
        />
      )}
      <CloseFlagModal flag={closing} onClose={() => setClosing(null)} onDone={() => onClosed?.()} />
    </div>
  );
};

export default FlagTable;
